import React, { useState } from 'react';
import translations from '../utils/translations';

const CollapsiblePanel = ({ 
  title, 
  children, 
  position, 
  defaultExpanded = true,
  width = '250px',
  backgroundColor = '#f0f4f8',
  current_locale = 'en',
  isRTL = false
}) => {
  // Get translations based on locale
  const t = translations[current_locale] || translations['en'];
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [maximized, setMaximized] = useState(false);
  
  // Determine if panel is on the left or right side
  const isLeft = position === 'left';
  const isRight = position === 'right';
  
  // Calculate collapsed width (just enough for the toggle button and title)
  const collapsedWidth = '40px';
  
  // Calculate width based on expanded and maximized states
  const calculateWidth = () => {
    if (!expanded) return collapsedWidth;
    if (maximized) return '100%';
    return width;
  };
  
  return (
    <div style={{ 
      width: calculateWidth(),
      backgroundColor: backgroundColor,
      height: '100%',
      overflow: 'hidden',
      transition: 'width 0.3s ease-in-out',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      borderRight: (isLeft && !isRTL) || (isRight && isRTL) ? '1px solid #e0e0e0' : 'none',
      borderLeft: (isRight && !isRTL) || (isLeft && isRTL) ? '1px solid #e0e0e0' : 'none',
      boxShadow: (isLeft && !isRTL) || (isRight && isRTL) 
        ? 'inset -2px 0 5px rgba(0,0,0,0.03)' 
        : (isRight && !isRTL) || (isLeft && isRTL) 
          ? 'inset 1px 0 3px rgba(0,0,0,0.05)'
          : 'none',
      zIndex: maximized ? 10 : 1,
    }}>
      {/* Header with toggle button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: expanded ? '15px' : '15px 0',
        borderBottom: '2px solid #3498db',
        backgroundColor: backgroundColor,
        position: 'relative',
        minHeight: '50px',
        boxSizing: 'border-box'
      }}>
        <button 
          onClick={() => setExpanded(!expanded)}
          style={{
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            marginRight: expanded ? '10px' : '0',
            transform: expanded 
              ? ((isLeft && !isRTL) || (isRight && isRTL) ? 'rotate(0deg)' : 'rotate(180deg)') 
              : ((isLeft && !isRTL) || (isRight && isRTL) ? 'rotate(180deg)' : 'rotate(0deg)'),
            transition: 'transform 0.3s ease',
            zIndex: 2,
            flexShrink: 0
          }}
        >
          {((isLeft && !isRTL) || (isRight && isRTL)) ? '◀' : '▶'}
        </button>
        
        {/* Only show maximize button for Netlist panel and when expanded */}
        {((isRight && !isRTL) || (isLeft && isRTL)) && expanded && (
          <button 
            onClick={() => setMaximized(!maximized)}
            style={{
              backgroundColor: '#2ecc71',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              marginRight: '10px',
              zIndex: 2,
              flexShrink: 0
            }}
          >
            {maximized ? '↙' : '↗'}
          </button>
        )}
        
        {expanded && (
          <h3 style={{ 
            margin: 0, 
            color: '#2c3e50',
            fontSize: '16px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {title}
          </h3>
        )}
        
        {/* Vertical title when collapsed */}
        {!expanded && (
          <div style={{
            position: 'absolute',
            top: '60px',
            left: '50%',
            transform: 'translateX(-50%) rotate(90deg)',
            transformOrigin: 'left center',
            whiteSpace: 'nowrap',
            color: '#2c3e50',
            fontWeight: '600',
            fontSize: '14px',
            width: '200px', // Enough width for the rotated text
            textAlign: 'center'
          }}>
            {title}
          </div>
        )}
      </div>
      
      {/* Content area */}
      <div style={{
        flex: 1,
        overflow: expanded ? 'auto' : 'hidden',
        padding: expanded ? '15px' : '0',
        display: expanded ? 'block' : 'none'
      }}>
        {children}
      </div>
    </div>
  );
};

export default CollapsiblePanel;
